import { ownerKeyOf } from "@/api/session";
import { useOnline } from "@/app/online";
import { useReloginDeferred } from "@/app/relogin";
import { useSession } from "@/app/session";
import maqiaLogo from "@/assets/brand/maqia-sinfondo-600.png";
import { contactPickerAvailable } from "@/lib/contactPicker";
import { absoluteDateTime } from "@/lib/datetime";
import {
  type OutboxFailure,
  type StaleOutboxItem,
  discardOutboxItem,
  retryOutboxItem,
  useOutbox,
} from "@/offline";
import { type QueryClient, useIsMutating, useQueryClient } from "@tanstack/react-query";
import { Info, Plus, TriangleAlert, WifiOff } from "lucide-react";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { FieldLeadForm } from "./FieldLeadForm";
import {
  type FieldLeadDraft,
  asManualDraft,
  clearFieldLeadDraft,
  emptyFieldLeadDraft,
  isEmptyFieldLeadDraft,
  loadFieldLeadDraft,
  nullAfterTimeout,
} from "./draftStore";
import {
  FIELD_LEAD_STATE_TEXT,
  type FieldLeadProblem,
  STALE_TEXT,
  UNCONFIRMED_HINT,
  WAITING_SESSION_TEXT,
  fieldLeadProblem,
  fieldLeadRows,
  legacyNoticeTexts,
  queuedFieldLeadProblem,
  staleKindText,
} from "./fieldLeadView";
import {
  fieldLeadHistory,
  forgetFieldLead,
  isFieldLeadRemembered,
  subscribeToFieldLeadHistory,
} from "./history";
import type { FieldLeadContactSource } from "./historyDb";
import { FIELD_LEAD_LOADS_KEY, fieldLeadLoadKey } from "./useFieldLeadLoad";

export const EMPTY_HISTORY_TEXT = "Todavía no cargaste ningún dato desde este teléfono.";
export const OFFLINE_DOOR_TEXT =
  "Sin señal: para cargar un dato hace falta conexión, porque la máquina se elige del stock del concesionario. Esperá a que vuelva.";
export const SENDING_PREVIOUS_TEXT = "Mandando el dato anterior…";
const DISCARD_HINT = "Descartarlo lo borra de este teléfono: el dato no llegó al CRM y se pierde.";

function FailedRow({
  failure,
  itemId,
  clientLoadId,
  contactSource,
}: {
  failure: OutboxFailure | null;
  itemId: string;
  clientLoadId: string;
  contactSource: FieldLeadContactSource | undefined;
}) {
  const problem = queuedFieldLeadProblem(failure, contactSource);

  async function discard(): Promise<void> {
    await forgetFieldLead(clientLoadId);
    await discardOutboxItem(itemId);
  }

  return (
    <>
      <span className="notice notice--error">
        <TriangleAlert size={16} aria-hidden="true" />
        <span>{problem.message}</span>
      </span>
      {problem.retriable ? (
        <button type="button" className="btn" onClick={() => void retryOutboxItem(itemId)}>
          Reintentar
        </button>
      ) : null}
      <DiscardButton onDiscard={discard} />
    </>
  );
}

function DiscardButton({ onDiscard }: { onDiscard: () => Promise<void> }) {
  const [confirming, setConfirming] = useState(false);

  return confirming ? (
    <>
      <span className="t-small">{DISCARD_HINT}</span>
      <button type="button" className="btn" onClick={() => void onDiscard()}>
        Sí, descartarlo
      </button>
    </>
  ) : (
    <button type="button" className="btn" onClick={() => setConfirming(true)}>
      Descartar
    </button>
  );
}

function StaleRow({ item }: { item: StaleOutboxItem }) {
  if (item.notices !== null) {
    return (
      <article className="card field-lead__row field-lead__row--sent">
        <StaleIdentity item={item} />
        {legacyNoticeTexts(item.notices).map((text) => (
          <span key={text} className="notice notice--info">
            <Info size={16} aria-hidden="true" />
            <span>{text}</span>
          </span>
        ))}
        <button type="button" className="btn" onClick={() => void discardOutboxItem(item.id)}>
          Entendido
        </button>
      </article>
    );
  }
  return (
    <article className="card field-lead__row field-lead__row--failed">
      <StaleIdentity item={item} />
      <span className="t-small">{FIELD_LEAD_STATE_TEXT.failed}</span>
      <span className="notice notice--error">
        <TriangleAlert size={16} aria-hidden="true" />
        <span>{STALE_TEXT}</span>
      </span>
      <DiscardButton onDiscard={() => discardOutboxItem(item.id)} />
    </article>
  );
}

function StaleIdentity({ item }: { item: StaleOutboxItem }) {
  return (
    <>
      <span className="t-body-strong">{staleKindText(item.kind)}</span>
      {item.label === null ? null : <span className="t-small">{item.label}</span>}
      <span className="t-small">{absoluteDateTime(item.occurredAt)}</span>
    </>
  );
}

function preferredSource(): FieldLeadContactSource {
  return contactPickerAvailable() ? "agenda" : "manual";
}

async function restorableDraft(
  ownerKey: string,
  inFlight: (clientLoadId: string) => boolean,
): Promise<FieldLeadDraft | null> {
  const saved = await loadFieldLeadDraft(ownerKey);
  if (saved === null || inFlight(saved.clientLoadId)) {
    return null;
  }
  if (await isFieldLeadRemembered(saved.clientLoadId).catch(() => false)) {
    await clearFieldLeadDraft(saved.clientLoadId);
    return null;
  }
  return saved.source === "agenda" && !contactPickerAvailable() ? asManualDraft(saved) : saved;
}

function failedLoadProblem(
  queryClient: QueryClient,
  draft: FieldLeadDraft,
): FieldLeadProblem | null {
  const attempts = queryClient
    .getMutationCache()
    .findAll({ mutationKey: fieldLeadLoadKey(draft.clientLoadId), exact: true });
  const last = attempts[attempts.length - 1];
  if (last === undefined || last.state.status !== "error") {
    return null;
  }
  return fieldLeadProblem(last.state.error, draft.source);
}

export function FieldLeadScreen() {
  const session = useSession();
  const online = useOnline();
  const ownerKey = ownerKeyOf(session.user.tenant_id, session.user.id);
  const records = useSyncExternalStore(
    subscribeToFieldLeadHistory,
    fieldLeadHistory,
    fieldLeadHistory,
  );
  const { items, stale } = useOutbox();
  const sessionDeferred = useReloginDeferred();
  const queryClient = useQueryClient();
  const [restored, setRestored] = useState(false);
  const [parked, setParked] = useState<FieldLeadDraft | null>(null);
  const [draft, setDraft] = useState<FieldLeadDraft | null>(null);
  const [draftProblem, setDraftProblem] = useState<FieldLeadProblem | null>(null);
  const sending = useIsMutating({ mutationKey: FIELD_LEAD_LOADS_KEY }) > 0;
  const sheetOpen = useRef(false);
  sheetOpen.current = draft !== null;

  useEffect(() => {
    let cancelled = false;
    if (sending || sheetOpen.current) {
      return;
    }
    setRestored(false);
    const inFlight = (clientLoadId: string) =>
      queryClient.isMutating({ mutationKey: fieldLeadLoadKey(clientLoadId), exact: true }) > 0;
    void nullAfterTimeout(restorableDraft(ownerKey, inFlight)).then((usable) => {
      if (cancelled) {
        return;
      }
      if (usable !== null && !sheetOpen.current) {
        setParked(usable);
        setDraftProblem(failedLoadProblem(queryClient, usable));
        setDraft(usable);
      }
      setRestored(true);
    });
    return () => {
      cancelled = true;
    };
  }, [ownerKey, queryClient, sending]);

  function openNew() {
    setDraftProblem(null);
    setDraft(emptyFieldLeadDraft(crypto.randomUUID(), preferredSource()));
    setParked(null);
  }

  function openDoor() {
    if (parked === null) {
      openNew();
      return;
    }
    if (records.some((record) => record.clientLoadId === parked.clientLoadId)) {
      void clearFieldLeadDraft(parked.clientLoadId);
      openNew();
      return;
    }
    if (
      queryClient.isMutating({ mutationKey: fieldLeadLoadKey(parked.clientLoadId), exact: true }) >
      0
    ) {
      openNew();
      return;
    }
    setDraftProblem(null);
    setDraft(parked);
  }

  function park(current: FieldLeadDraft) {
    setParked(isEmptyFieldLeadDraft(current) ? null : current);
    setDraft(null);
  }

  function forgetDelivered(clientLoadId: string) {
    setParked((current) => (current?.clientLoadId === clientLoadId ? null : current));
  }

  function discard() {
    setParked(null);
    setDraft(null);
  }

  const rows = fieldLeadRows(records, items);

  return (
    <section className="screen field-lead">
      <div className="field-lead__door">
        <img className="field-lead__logo" src={maqiaLogo} alt="MAQIA" width={600} height={200} />
        <button
          type="button"
          className="btn btn--primary field-lead__open"
          disabled={!restored || sending}
          aria-busy={!restored || sending ? "true" : undefined}
          onClick={openDoor}
        >
          <Plus size={28} aria-hidden="true" />
          {sending
            ? SENDING_PREVIOUS_TEXT
            : parked === null
              ? "Cargar un dato"
              : "Seguir con el dato"}
        </button>
      </div>

      {online ? null : (
        <p className="notice notice--offline">
          <WifiOff size={16} aria-hidden="true" />
          <span>{OFFLINE_DOOR_TEXT}</span>
        </p>
      )}

      <h2 className="t-eyebrow">Lo que cargaste</h2>

      {rows.length === 0 && stale.length === 0 ? (
        <p className="t-small">{EMPTY_HISTORY_TEXT}</p>
      ) : (
        <ul className="cards">
          {rows.map((row) => (
            <li key={row.record.clientLoadId}>
              <article className={`card field-lead__row field-lead__row--${row.state}`}>
                <span className="t-body-strong">{row.record.contactName}</span>
                <span className="t-small">{row.record.machineLabel}</span>
                <span className="t-small">
                  {(row.state === "waiting" || row.state === "retrying") && sessionDeferred
                    ? WAITING_SESSION_TEXT
                    : FIELD_LEAD_STATE_TEXT[row.state]}
                </span>
                {row.state === "unconfirmed" ? (
                  <span className="t-small">{UNCONFIRMED_HINT}</span>
                ) : null}
                {row.state === "failed" && row.outboxId !== null ? (
                  <FailedRow
                    failure={row.failure}
                    itemId={row.outboxId}
                    clientLoadId={row.record.clientLoadId}
                    contactSource={row.record.contactSource}
                  />
                ) : null}
              </article>
            </li>
          ))}
          {stale.map((item) => (
            <li key={item.id}>
              <StaleRow item={item} />
            </li>
          ))}
        </ul>
      )}

      {draft === null ? null : (
        <FieldLeadForm
          key={draft.clientLoadId}
          ownerKey={ownerKey}
          initial={draft}
          initialProblem={draftProblem}
          onPark={park}
          onDelivered={forgetDelivered}
          onDiscard={discard}
          onLoadAnother={openNew}
        />
      )}
    </section>
  );
}
